// Keeps a full copy of the catalog (home, categories, shops, products and
// their images) on the phone, and silently refreshes only what changed.
import { Capacitor } from "@capacitor/core";
import { api } from "./api";
import { readCache, writeCache } from "./swr";
import { precacheMedia, collectBundleMedia } from "./mediaCache";

const POLL_MS = 30_000;
const notify = (key) => window.dispatchEvent(new CustomEvent("kh:cache-updated", { detail: { key } }));

async function pull(key, path, v) {
  const { data } = await api.get(path, { params: { _v: v } });
  writeCache(key, data);
  notify(key);
  return data;
}

const homeMedia = (d) => [
  ...(d.shops || []).flatMap((s) => [s.logo, s.cover_image]),
  ...(d.categories || []).map((c) => c.image),
  ...(d.promotions || []).map((p) => p.image),
  ...(d.featured || []).flatMap((s) => [s.logo, s.cover_image]),
].filter(Boolean);

let running = false;
let progressBus = (p) => { try { window.dispatchEvent(new CustomEvent("kh:sync-progress", { detail: p })); } catch { /* ignore */ } };

export function onSyncProgress(cb) {
  const on = (e) => cb(e.detail);
  window.addEventListener("kh:sync-progress", on);
  return () => window.removeEventListener("kh:sync-progress", on);
}

export async function syncCatalog(force = false) {
  if (running || document.hidden || navigator.onLine === false) return;
  running = true;
  try {
    const { data: m } = await api.get("/sync/manifest", { params: { _t: Date.now() } });
    const prev = readCache("sync-manifest");
    const homeChanged = force || !prev || m.home !== prev.home;
    const allChanged = force || !prev || m.all !== prev.all;
    let ok = true;
    if (homeChanged) {
      const tag = `${m.home}`;
      progressBus({ phase: "home", done: 0, total: 1, label: "تحميل الواجهة الرئيسية" });
      const home = await pull("home-init", "/home-init", tag);
      precacheMedia(homeMedia(home));
      await pull("categories-images", "/categories/images", tag);
      await pull("categories", "/categories", tag);
      await pull("shops", "/shops", tag);
      progressBus({ phase: "home", done: 1, total: 1 });
    }
    const shops = readCache("shops") || readCache("home-init")?.shops || [];
    const stale = [];
    for (const s of shops) {
      const sv = m.shops?.[s.id] || 0;
      if (force || allChanged || sv !== (prev?.shops?.[s.id] || 0) || !readCache(`shop-bundle:${s.id}`)) stale.push({ s, sv });
    }
    for (let i = 0; i < stale.length; i++) {
      const { s, sv } = stale[i];
      progressBus({ phase: "shops", done: i, total: stale.length, label: s.name || "تحميل المحلات" });
      try {
        const b = await pull(`shop-bundle:${s.id}`, `/shops/${s.id}/bundle`, `${m.all}.${sv}`);
        precacheMedia(collectBundleMedia(b));
        const sec = await pull(`shop-sections:${s.id}`, `/shops/${s.id}/sections`, `${m.all}.${sv}`);
        precacheMedia((sec?.sections || []).map((x) => x.image).filter(Boolean));
        await pull(`shop-services:${s.id}`, `/shops/${s.id}/services`, `${m.all}.${sv}`);
      } catch { ok = false; }
      await new Promise((r) => setTimeout(r, Capacitor.isNativePlatform() ? 60 : 150));
    }
    progressBus({ phase: "done", done: stale.length, total: stale.length });
    if (ok) writeCache("sync-manifest", m);
  } catch { progressBus({ phase: "error" }); }
  finally { running = false; }
}

// True if the phone has NEVER completed a catalog sync (first launch after
// install). The preloader uses this to show the "preparing" splash.
export function isFirstLaunch() {
  return !readCache("sync-manifest") && !readCache("home-init");
}

export function startOfflineSync() {
  setTimeout(() => syncCatalog(), 1200);
  setInterval(() => syncCatalog(), POLL_MS);
  window.addEventListener("online", () => syncCatalog());
  document.addEventListener("visibilitychange", () => { if (!document.hidden) syncCatalog(); });
  let t = null;
  window.addEventListener("kh:data-mutated", () => { clearTimeout(t); t = setTimeout(() => syncCatalog(), 1500); });
}
